import React, { useState } from 'react';
import { Box, IconButton, Tooltip, useMediaQuery, useTheme, Snackbar, Alert } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Message } from '../../models/Message';
import MessageDeleteDialog from './MessageDeleteDialog';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';

interface MessageActionsProps {
  message: Message;
  userId: string;
  subsequentMessagesCount: number;
  onDeleteMessage: (messageId: string, createdAt: string) => void;
  isConversationBusy: boolean;
  isFirstMessage?: boolean;
}

/**
 * Renders copy and delete actions for a message.
 */
const MessageActions: React.FC<MessageActionsProps> = ({
  message,
  userId,
  subsequentMessagesCount,
  onDeleteMessage,
  isConversationBusy,
  isFirstMessage = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useUntypedTranslation();

  // Track deletion confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Track warning message for bot message deletion attempts
  const [showWarning, setShowWarning] = useState(false);

  // Track copy success message
  const [copySuccess, setCopySuccess] = useState(false);

  // Whether this is a user message (can be deleted)
  const isUserMessage = message.sender === 'user';

  // Determine icon sizes for mobile/desktop
  const iconSize = isMobile ? 'small' : 'medium';
  const iconStyle = {
    fontSize: isMobile ? '1rem' : '1.25rem',
    padding: isMobile ? '4px' : '8px',
  };

  // Handle click on delete button
  const handleDeleteClick = () => {
    // Skip if conversation is busy
    if (isConversationBusy) return;

    // Check if this is the first message
    if (isFirstMessage) {
      // Show warning that conversation must be deleted instead
      setShowWarning(true);
      return;
    }

    // Open delete confirmation dialog for user messages
    setDeleteDialogOpen(true);
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message.text);
      setCopySuccess(true);
      // Auto-hide the success message after 2 seconds
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy message to clipboard:', error);
    }
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Confirm message deletion
  const handleConfirmDelete = () => {
    if (isUserMessage && !isFirstMessage) {
      onDeleteMessage(message.id, message.createdAt);
    }
    setDeleteDialogOpen(false);
  };

  // Close warning
  const handleCloseWarning = () => {
    setShowWarning(false);
  };

  // Get tooltip text for delete button (only shown for user messages)
  const getDeleteTooltip = () => {
    if (isConversationBusy) {
      return t('messageActions.processingTooltip');
    } else if (isFirstMessage) {
      return t('messageActions.deleteFirstMessageTooltip');
    } else {
      return t('messageActions.deleteTooltip');
    }
  };

  // Get warning message (only for first message case)
  const getWarningMessage = () => {
    return t('messageActions.warningFirstMessage');
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mt: 1,
          opacity: isConversationBusy ? 0.5 : 0.7,
          '&:hover': {
            opacity: isConversationBusy ? 0.5 : 1,
          },
        }}
      >
        {/* Copy icon */}
        <Tooltip title={t('messageActions.copyTooltip') || 'Copy to clipboard'}>
          <span>
            <IconButton
              onClick={handleCopyToClipboard}
              size={iconSize}
              sx={{
                color: 'text.secondary',
                ...iconStyle,
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </span>
        </Tooltip>

        {/* Delete button - only shown for user messages */}
        {isUserMessage && (
          <Tooltip title={getDeleteTooltip()}>
            <span>
              {' '}
              {/* Use span to allow disabled tooltip */}
              <IconButton
                onClick={handleDeleteClick}
                size={iconSize}
                disabled={isConversationBusy || isFirstMessage}
                sx={{
                  color: 'text.secondary',
                  ...iconStyle,
                  '&.Mui-disabled': {
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Deletion confirmation dialog - only shown for user messages */}
      <MessageDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        messageCount={subsequentMessagesCount + 1} // Include this message
      />

      {/* Warning snackbar for bot message deletion attempts */}
      <Snackbar
        open={showWarning}
        autoHideDuration={5000}
        onClose={handleCloseWarning}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseWarning} severity="info" sx={{ width: '100%' }}>
          {getWarningMessage()}
        </Alert>
      </Snackbar>

      {/* Copy success snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopySuccess(false)} severity="success" sx={{ width: '100%' }}>
          {t('messageActions.copiedToClipboard') || 'Message copied to clipboard'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default React.memo(MessageActions);
